import * as React from 'react';
import {
  Plugins,
  TypeImplementation,
  Types,
  Component,
} from '@packages/systems/wfdl';
import {
  a,
  TList,
  TTypeConstructor,
  TTypeApplication,
  EList,
  b,
  c,
  ELiteral,
} from '@packages/systems/wfdl-ast';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {updateExpressionAction} from '@packages/systems/ExpressionEditor-utils';
import {
  ContextualExpression,
  ContextualExpressions,
  getAnn,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {Autocomplete} from '@packages/systems/spring';
import {getStyleVariantTypeArguments} from '@packages/systems/component-style-variants';
import {selectComponents} from '@packages/utilities/platform-selectors';
import {useDesignerSelector} from '@packages/systems/designer/flux';

export const TGenericStyleVariant = TTypeApplication({
  con: TTypeApplication({
    con: TTypeApplication({
      con: TTypeConstructor(qualify(Plugins.name('Basic'), 'StyleVariant')),
      arg: a,
    }),
    arg: b,
  }),
  arg: c,
});

export const TGenericStyleVariantAnyOfList = TTypeApplication({
  con: TTypeApplication({
    con: TTypeApplication({
      con: TTypeConstructor(
        qualify(Plugins.name('Basic'), typeName('StyleVariantAnyOfList'))
      ),
      arg: a,
    }),
    arg: b,
  }),
  arg: c,
});

const getVariantOptions = (
  pluginName: string,
  componentName: string,
  styleVariantId: string,
  componentMap: Component.ComponentMap
) => {
  const component = componentMap.get(qualify(pluginName, componentName));
  if (!component) {
    return null;
  }
  const variants = component.variants;
  if (!variants) {
    return null;
  }
  return variants[styleVariantId];
};

function RenderStyleVariantAnyOfList({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: TypeImplementation.ExpressionEditorDispatch;
}) {
  const componentMap = useDesignerSelector(selectComponents);

  const currentValuesExpr = ContextualExpressions.getList(stripAnn(expr));
  const currentValues = currentValuesExpr
    ? currentValuesExpr
        .map((item) => ContextualExpressions.getLiteral(stripAnn(item))?.value)
        .filter((value): value is string => value !== null)
    : [];
  const exprAnn = getAnn(expr);
  const type = exprAnn.type;
  const deconstructedType = type
    ? Types.deconstructTypeApplication(type)
    : null;
  const styleVariantType = deconstructedType ? deconstructedType.args[0] : null;
  if (!styleVariantType) {
    return null;
  }
  const styleVariantListDispatch = (values: string[]) => {
    const list = values.map((value) =>
      ELiteral({
        name: qualify(Plugins.name('Basic'), typeName('StyleVariant')),
        value,
      })
    );
    dispatch(updateExpressionAction(EList(list)));
  };
  const {variantFieldId, componentName, pluginName} = styleVariantType
    ? getStyleVariantTypeArguments(styleVariantType)
    : {
        variantFieldId: undefined,
        componentName: undefined,
        pluginName: undefined,
      };
  const variantOptions =
    variantFieldId && componentName && pluginName
      ? getVariantOptions(
          pluginName,
          componentName,
          variantFieldId,
          componentMap
        )
      : null;

  const options = variantOptions
    ? Object.values(variantOptions).map((styleVariant) => {
        return {
          value: styleVariant.id,
          label: styleVariant.displayName,
        };
      })
    : [];

  return (
    <Autocomplete
      aria-label="Select options"
      data-automation-id={`${Types.generateAutomationIds(styleVariantType)}`}
      value={currentValues}
      selectionMode="multiple"
      options={options}
      onChange={(value) => {
        styleVariantListDispatch(value);
      }}
    />
  );
}

export const StyleVariantAnyOfList = TypeImplementation.Alias({
  params: ['a', 'b', 'c'],
  type: TList(TGenericStyleVariant),
  render: RenderStyleVariantAnyOfList,
});
